import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Autocomplete,
  Chip,
  Tooltip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Http from "../../../services/Http";
import dataService from "../../../services/data.service";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useHMG } from "../../../HMGContext";
import DateFormatter from "../../../hooks/DateFormatter";
import LoadingScreen from "../LoadingScreen";
import { handleApiError } from "../../../Utils/errorHandler";
import { useTpxPermissions } from "../../../contexts/TpxPermissionsContext";
import { LOCK_PERMISSION_DENIED } from "../../../services/locks.service";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const PlanVersionModal = ({
  isOpen,
  setOpenVersionModal,
  planParams,
  setCreateNewPlan,
  pageSpecificLoader = null,
  isNewVersion,
  planDetailsRowData,
  showToast = () => { },
  closeToast,
  setLoading,
  setUpdatePlanDetailsPage=null,
  fromAllPlansPage=false,
  fetchLatestPlan,
  handleSuccessfulUpdate,
  setAllPlansPageReload,
  modalOpenedFrom = null
}) => {
  const [formData, setFormData] = useState({});
  const [isMarkComplete, setIsMarkComplete] = useState(false);
const [initialCompletedState, setInitialCompletedState] = useState(false);
const [isLoading, setIsLoading] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const [isUpdateChangesButtonDisabled, setIsUpdateChangesButtonDisabled] =
    useState(true);
  const [minDate, setMinDate] = useState(dayjs());
  const [maxDate, setMaxDate] = useState(dayjs());
  const [defaultMonthYear, setDefaultMonthYear] = useState(
    dayjs(new Date(planParams?.fiscalYear, planParams?.entryMonth - 1))
  );
  const [defaultASOnDate, setDefaultASOnDate] = useState( planParams?.asOnDate ? dayjs(String(planParams?.asOnDate), 'YYYYMMDD') : null);
  const { lockPlan } = useTpxPermissions();
  const lockPermissionTooltipPlan = LOCK_PERMISSION_DENIED.plan;
  const publishedStatusCheckboxDisabled =
    !lockPlan ||
    (!isNewVersion && planParams?.readOnly && formData.readOnly !== false);

  let currentWeekOfMonth = Math.ceil(
    (defaultMonthYear.date() + defaultMonthYear.startOf("month").day()) / 7
  );

  const {setCurrentModalName} = useHMG();

  useEffect(() => {
    // if (!isOpen) {
    //   closeToast();
    // }
    if (planParams) {
      setFormData({
        versionName: planParams.versionName,
        // revenueType: planParams.revenueType,
        planStatus: isNewVersion ? "Draft" : planParams.planStatus,
        autoRefreshRevDaily: planParams.autoRefreshRevDaily,
        readOnly: planParams.readOnly,
        entryMonth: planParams.entryMonth,
        entryWeek: planParams.entryWeek,
        asOnDate : planParams.asOnDate,
      });
      const isCompleted = planParams?.completedDateNumber != null;
      setIsMarkComplete(isCompleted);
      setInitialCompletedState(isCompleted);
      setMinDate(dayjs(new Date(planParams.fiscalYear, 0, 1)));
      setMaxDate(dayjs(new Date(planParams.fiscalYear, 11, 1)));
      setDefaultMonthYear(
        dayjs(new Date(planParams.fiscalYear, planParams.entryMonth - 1))
      );
      if(planParams?.asOnDate) {
        setDefaultASOnDate(dayjs(String(planParams?.asOnDate), 'YYYYMMDD'));
      }
      currentWeekOfMonth = Math.ceil(
        (defaultMonthYear.date() + defaultMonthYear.startOf("month").day()) / 7
      );
      setIsUpdateChangesButtonDisabled(true);
    }
  }, [planParams, isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      (modalOpenedFrom === 'dueDate' ||
        modalOpenedFrom === 'planStatus' ||
        modalOpenedFrom === 'readOnly' ||
        modalOpenedFrom === 'completed')
    ) {
      setShowPulse(true);
      const timer = setTimeout(() => {
        setShowPulse(false);
      }, 10000);
      return () => clearTimeout(timer);
    } else {
      setShowPulse(false);
    }
  }, [isOpen, modalOpenedFrom]);

  const getCurrentDateNumber = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  const updateMarkCompleteStatus = async () => {
    try {
      const planIds = planParams.id;
      let apiUrl;
  
      if (isMarkComplete && formData.asOnDate) {
        // Mark as complete - include dateNumber
        const dateNumber = getCurrentDateNumber();
        apiUrl = `/plan/updated-plans-completed-status?planIds=${planIds}&dateNumber=${dateNumber}`;
      } else {
        // Unmark as complete - don't include dateNumber
        apiUrl = `/plan/updated-plans-completed-status?planIds=${planIds}`;
      }
  
      const response = await Http.PUT(apiUrl, {});
      const versionName = planParams?.versionName || `V${planParams?.versionNumber}`;
      if (response.status === 200) {
        const message = `'${versionName}' version settings updated.`;
        showToast(message, 'success');
        return true;
      } else {
        showToast(`Failed to update the '${versionName}' version settings`, 'error');
        return false;
      }
    } catch (error) {
      const versionName = planParams?.versionName || `V${planParams?.versionNumber}`;
      handleApiError(error, showToast, `Failed to update the '${versionName}' version settings`);
      return false;
    }
  };

  const handleClose = () => {
    setOpenVersionModal(false);
    setCurrentModalName(null);
    setDefaultASOnDate(planParams?.asOnDate ? dayjs(String(planParams?.asOnDate), 'YYYYMMDD') : null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (isUpdateChangesButtonDisabled) setIsUpdateChangesButtonDisabled(false);
  };

const handleAsOnDateChange = (newValue) => {
  if (!newValue) {
    setDefaultASOnDate(null);
    setFormData((prev) => ({
      ...prev,
      asOnDate: null,
    }));
    setIsUpdateChangesButtonDisabled(false); // if you want enable save button on clear
    return;
  }

  const formattedDate = newValue.format('YYYYMMDD');
  if (formattedDate === defaultASOnDate?.format('YYYYMMDD')) return;

  setIsUpdateChangesButtonDisabled(false);

  setDefaultASOnDate(newValue);
  setFormData((prev) => ({
    ...prev,
    asOnDate: formattedDate,
  }));
  
  
};

  const handleMonthChange = (newValue) => {
    const updatedMonth = newValue.$M + 1;
    setFormData((prev) => ({
      ...prev,
      entryMonth: updatedMonth,
    }));
    if (isUpdateChangesButtonDisabled) setIsUpdateChangesButtonDisabled(false);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
    if (name === "planStatus") {
      setFormData((prev) => ({
        ...prev,
        planStatus: checked ? "Published" : "Draft",
      }));
    }
    setIsUpdateChangesButtonDisabled(false);
  };

  const createNewVersion = async () => {
    setIsLoading(true);
    // Open the target tab synchronously inside the click gesture so the browser's popup blocker allows it;
    // it is redirected to the new version once the create call returns, or closed if creation fails.
    const newVersionTab = window.open("", "_blank");
    const data = {
      planDTO: {
        parentPlanId: planParams.id,
        coaVersion: planParams.coaVersion ?? planParams.coaVersionId,
        propertyIds: planParams.propertyIds,
        fiscalYear: planParams.fiscalYear,
        planType: planParams.planType,
        planDescription: planParams.planDescription,
        planStatus: formData.planStatus,
        // revenueType: formData.revenueType,
        versionName: formData.versionName,
        readOnly: formData.readOnly,
        autoRefreshRevDaily: formData.autoRefreshRevDaily,
        entryMonth: formData.entryMonth || planParams.entryMonth,
        entryWeek: formData.entryWeek || planParams.entryWeek,
        startDateNumber: planParams.startDateNumber,
        endDateNumber: planParams.endDateNumber,
        asOnDate: formData.asOnDate || planParams.asOnDate,
        planHighlightsStatistics: planParams.planHighlightsStatistics,
        planHighlightsRevenue: planParams.planHighlightsRevenue,
        planHighlightsTotals: planParams.planHighlightsTotals,
        createdBy: dataService.getFullUserName(),
        createdOn: new Date(),
        modifiedBy: dataService.getFullUserName(),
        modifiedOn: new Date(),
        planTypeId: planParams.planTypeId,
        organizationId: dataService.getSelectedHMGId(),
        ccmsTemplateId: planParams.ccmsTemplateId
      },
      planDetails: planDetailsRowData,
    };

    try {
      // setCurrentModalName(null)
      // setOpenVersionModal(false);
      // setShowGlobalLoader(true);
      const resp = await Http.POST("plan-details/create-new-version", data);
      if (resp && resp.status === 200) {
        const newVersionId = resp.data;
        setCreateNewPlan(new Date());
        // setAllPlansPageReload(new Date());
        showToast(
          `'${formData.versionName}' Version created successfully`,
          "success",
          null,
          6000
        );
        // Open the newly created version in the pre-opened tab.
        if (newVersionTab) {
          if (newVersionId != null) {
            newVersionTab.location.href = `/planVersion/${newVersionId}`;
          } else {
            newVersionTab.close();
          }
        }
      } else {
        if (newVersionTab) newVersionTab.close();
        showToast(
          `Failed to create the '${planParams.versionName}' version.`,
          "error",
          null,
          6000
        );
      }
    } catch (error) {
      if (newVersionTab) newVersionTab.close();
      handleApiError(error, showToast, `Failed to create the '${planParams.versionName}' version.`);
    } finally {
      // setShowGlobalLoader(false);
      setIsLoading(false);
      setCurrentModalName(null)
      setOpenVersionModal(false);
    }
  };

  const updatePlanSettings = async () => {
    const data = {
      id: planParams.id,
      planId: planParams.id,
      propertyIds: planParams.propertyIds,
      fiscalYear: planParams.fiscalYear,
      planStatus: formData.planStatus,
      planType: planParams.planType,
      // revenueType: formData.revenueType,
      versionName: formData.versionName,
      readOnly: formData.readOnly,
      autoRefreshRevDaily: formData.autoRefreshRevDaily,
      startDateNumber: planParams.startDateNumber,
      endDateNumber: planParams.endDateNumber,
      entryMonth: formData.entryMonth || planParams.entryMonth,
      entryWeek: formData.entryWeek || planParams.entryWeek,
      asOnDate: formData.asOnDate,
      modifiedBy:
        dataService.getUser().firstName + " " + dataService.getUser().lastName,
      modifiedOn: new Date(),
    };
    if(setLoading != null) {
      setLoading(true);
    }
    try {
      // First update plan settings
      const resp = await Http.POST("plan/update-plan-settings", data);
      if (resp && resp.status === 200) {
        
        // Check if mark complete status changed
        if (isMarkComplete !== initialCompletedState) {
          const markCompleteSuccess = await updateMarkCompleteStatus();
          // Don't show any message for mark complete status - it will be handled by updateMarkCompleteStatus function
          fetchLatestPlan &&  fetchLatestPlan()
        } else {
          showToast(
            `'${formData.versionName}' version settings updated.`,
            "success",
            null,
            6000
          );
        }
        
        setOpenVersionModal(false);
        setCreateNewPlan(new Date());
        if (fromAllPlansPage && handleSuccessfulUpdate) {
          handleSuccessfulUpdate();
        }
        if(setUpdatePlanDetailsPage != null) {
          setTimeout(() => {
            setUpdatePlanDetailsPage(new Date())
          }, 1000);
        }

        planParams.asOnDate = formData.asOnDate;
      } else {
        showToast(
          `Failed to update the '${formData.versionName}' version settings`,
          "error",
          null,
          6000
        );
      }
    } catch (error) {
      const versionName = formData?.versionName || planParams?.versionName || `V${planParams?.versionNumber}`;
      handleApiError(
        error,
        showToast,
        `Failed to update the '${versionName}' version settings`
      );
    } finally {
      if (setLoading != null) {
        setLoading(false);
      }
      setCurrentModalName(null)
    }
  };

  if (!planParams) return null;

  const pulseStyle = showPulse ? {
    animation: 'pulseHighlight 2s ease-in-out infinite',
    borderRadius: '8px',
  } : {};

  return (
    <>
      <style>{`
        @keyframes pulseHighlight {
        0% { background-color: #2A5BA214; }
        50% { background-color: #2A5BA229; }
        100% { background-color: #2A5BA214; }
        }
      `}</style>
      <Dialog
        open={isOpen}
        // onClose={handleClose}
        aria-labelledby="planversion-dialog-title"
        aria-describedby="planversion-dialog-description"
        PaperProps={{
          sx: {
            width: 600,
            borderRadius: 2,
            p: 2,
            boxShadow: 24,
            bgcolor: "#FFFFFF",
            position: "relative",
            overflow: "auto",
          },
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <DialogTitle
              id="planversion-dialog-title"
              variant="h4"

              sx={{
                p: 0,
                width: 496,
                fontSize: "24px",
                lineHeight: "36px",
                fontWeight: 700,
                color: "#212B36",
              }}
              display="flex"
              gap="8px"
            >
              {isNewVersion ? "Save as new version" : "Version settings"}
                <div className={`${!isNewVersion && planParams.readOnly ? "flex" : "hidden"}`}>
                  <Box
                    sx={{
                      "& .MuiChip-root": {
                        backgroundColor: "#919EAB29",
                        color: "#637381",
                        borderRadius: "6px",
                        fontWeight: "700",
                        width: "fit-content"
                      },
                    }}
                    className="!w-max"
                  >
                    <Chip size="small" label={"Read only"} />
                  </Box>
                  </div>
            </DialogTitle>
            <IconButton
              onClick={handleClose}>
              <ClearIcon sx={{ width: "16px", height: "16px" }} />
            </IconButton>
          </Box>
          <Typography
            sx={{ color: "#637381", fontSize: "12px", fontWeight: 400 }}
          >
            {dataService.getPropertyById(planParams.propertyIds)?.name ||
              "Error"}{" "}
            | {planParams.planType} | {planParams.fiscalYear}
          </Typography>
          <Typography
            sx={{ color: "#637381", fontSize: "12px", fontWeight: 400 ,marginTop: "4px"}}
          >
           *Required fields
          </Typography>
          {planParams?.sourceName && (
            <Typography
              sx={{
                color: "#637381",
                fontSize: "12px",
                fontWeight: 400,
                mt: "4px" // 18px space above
              }}
            >
              <div>
                <div className="!text-[12px] font-medium leading-[18px]">
                  Originally created in {planParams.sourceName} (Last edited in {
                    new Date(planParams?.sourceModifiedOn).getTime() < new Date(planParams?.modifiedOn).getTime()
                      ? "Trueplan"
                      : planParams?.sourceName
                  } on {DateFormatter({
                    value: new Date(planParams?.modifiedOn),
                    showTimeAgo: false,
                    outputFormat: "string"
                  })})
                </div>
                <div className="!text-[10px] leading-[14px]">
                  Note: Some logic or drivers were adjusted when brought into TruePlan.
                </div>
              </div>
            </Typography>
          )}
        </Box>

        <DialogContent sx={{ mt: 2, p: 0 }}>
          <Box>
            {(isLoading) && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1000,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LoadingScreen
                  title="Creating a plan new version..."
                  position="relative"
                />

              </Box>
            )}
          </Box>
          <Box sx={{ pt: 1, width: "568px" }}>
            <TextField
              size="small"
              id="version-name"
              label={isNewVersion ? "New version name*" : "Version name*"}
              variant="outlined"
              name="versionName"
              value={formData.versionName || ""}
              onChange={handleChange}
              disabled={!isNewVersion && planParams.readOnly}
              sx={{
                width: "568px",
                height: "40px",
                fontWeight: "600",
                "& .MuiInputLabel-root": {
                  fontWeight: 600,
                   fontSize: "14px",
                  color: !isNewVersion && planParams.readOnly ? "#919EABCC !important" : ""     
                },
                ".MuiOutlinedInput-root":{
                  fontSize: "14px"
                },
                  "& .MuiInputBase-input.Mui-disabled": {
                  color: "#919EABCC", // Custom color for disabled input text
                  WebkitTextFillColor: "#919EABCC", // Ensures compatibility with Webkit browsers
                },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: !isNewVersion && planParams.readOnly ? "#919EAB33 !important" : "",  
                    borderStyle: !isNewVersion && planParams.readOnly ? "dashed !important" : "solid", 
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: !isNewVersion && planParams.readOnly ? "#919EAB33 !important" : ""  
                  },
              }}
            />  
            {/* {!isNewVersion && (
              <FormControl
                fullWidth
                size="small"
                sx={{
                  mt: 2,
                  width: "568px",
                  "& .MuiInputLabel-root": { fontWeight: 600 },
                }}
              >
                <InputLabel id="revenue-type-label">Revenue type*</InputLabel>
                <Select
                  labelId="revenue-type-label"
                  id="revenue-type"
                  name="revenueType"
                  value={formData.revenueType || ""}
                  onChange={handleChange}
                  label="Revenue type"
                  disabled={!isNewVersion && planParams.readOnly}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            )} */}
            <Box sx={{ width: "100%", ...(showPulse && modalOpenedFrom === 'dueDate' ? pulseStyle : {}) }} className="flex items-center gap-4 pb-4 mt-4 border-[#919EAB33] border-y">
            <div className="w-[60%]">
              <FormControl
                className="flex"
                fullWidth
                size="small"
                sx={{ mt: 2, display: "flex", flexDirection: "row", gap: 2 }}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ flex: 1, "& .MuiInputLabel-root": { fontWeight: 600 } }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Due date"
                      value={defaultASOnDate|| null}
                      name="asOnDate"
                      onChange={handleAsOnDateChange}
                      minDate={dayjs()}
                      // maxDate={maxDate}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: false,
                          InputProps: {
                            readOnly: true,   // prevents typing in the text box
                          },
                        },
                        field: { readOnly: true },
                        actionBar: { actions: ['clear', 'accept'] },
                      }}
                      sx={{
                        "& .MuiInputLabel-root": {
                          fontSize: "14px",
                           color: !isNewVersion && planParams.readOnly ? "#919EABCC !important" : ""     
                       },
                         "& .MuiInputBase-input.Mui-disabled": {
                         color: "#919EABCC", // Custom color for disabled input text
                         WebkitTextFillColor: "#919EABCC", // Ensures compatibility with Webkit browsers
                       },
                         "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: !isNewVersion && planParams.readOnly 
                          ? "#919EAB33 !important" 
                          : modalOpenedFrom === 'dueDate' 
                            ? "#2A5BA2 !important" 
                            : "",  
                           borderStyle: !isNewVersion && planParams.readOnly ? "dashed !important" : "solid", 
                         },
                         "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: !isNewVersion && planParams.readOnly 
                          ? "#919EAB33 !important" 
                          : modalOpenedFrom === 'dueDate' 
                            ? "#2A5BA2 !important" 
                            : ""  
                         },
                       ".MuiSelect-outlined":{
                         fontSize: "14px !important",
                         lineHeight: "20px !important"
                        },
                        ".MuiOutlinedInput-root":{
                          fontSize: "14px"
                      }
                      }}
                      disabled={!isNewVersion && planParams.readOnly}
                    />
                    {defaultASOnDate && (
                      <IconButton
                        aria-label="clear date"
                        onClick={() => handleAsOnDateChange(null)}
                        size="small"
                        sx={{
                          position: "absolute",
                          right: 36,
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 1,
                        }}
                      >
                        <ClearIcon sx={{ fontSize: 18, color: "error.main" }} />
                      </IconButton>
                    )}
                  </LocalizationProvider>
                </FormControl>
              </FormControl>
              </div>
              <div className="fit-contant mt-4">
                <Box
                  sx={{
                    ...(showPulse && modalOpenedFrom === 'completed' ? pulseStyle : {}),
                    py: '4px',
                    width: '180px',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={defaultASOnDate ? isMarkComplete : null}
                        onChange={(e) => {
                          setIsMarkComplete(e.target.checked);
                          if (isUpdateChangesButtonDisabled) setIsUpdateChangesButtonDisabled(false);
                        }}
                        name="markComplete"
                        sx={{
                          m: "1px",
                          '&.Mui-checked': {
                            color: !isNewVersion && planParams.readOnly ? '#A7B1BC' : '#2A5BA2',
                          },
                        }}
                        disabled={(!isNewVersion && planParams.readOnly) || defaultASOnDate == null}
                      />
                    }
                    label="Mark complete"
                    sx={{
                      display: "block",
                      ".MuiFormControlLabel-label": {
                        fontSize: "14px !important",
                        color: !isNewVersion && planParams.readOnly ? "#919EAB !important" : "",
                      },
                    }}
                  />
                </Box>
              </div>
            </Box>
            {!isNewVersion && (planParams.planType === "Forecast" ) && minDate && (
              <FormControl
                className="flex"
                fullWidth
                size="small"
                sx={{ mt: 2, display: "flex", flexDirection: "row", gap: 2 }}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ flex: 1, "& .MuiInputLabel-root": { fontWeight: 600 } }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Month*"
                      value={defaultMonthYear}
                      name="entryMonth"
                      onChange={handleMonthChange}
                      minDate={minDate}
                      maxDate={maxDate}
                      views={["month"]}
                      slotProps={{
                        textField: {
                          size: "small",
                        },
                      }}
                      sx={{
                        "& .MuiInputLabel-root": {
                          fontSize: "14px",
                           color: !isNewVersion && planParams.readOnly ? "#919EABCC !important" : ""     
                       },
                         "& .MuiInputBase-input.Mui-disabled": {
                         color: "#919EABCC", // Custom color for disabled input text
                         WebkitTextFillColor: "#919EABCC", // Ensures compatibility with Webkit browsers
                       },
                         "& .MuiOutlinedInput-notchedOutline": {
                           borderColor: !isNewVersion && planParams.readOnly ? "#919EAB33 !important" : "",  
                           borderStyle: !isNewVersion && planParams.readOnly ? "dashed !important" : "solid", 
                         },
                         "&:hover .MuiOutlinedInput-notchedOutline": {
                           borderColor: !isNewVersion && planParams.readOnly ? "#919EAB33 !important" : ""  
                         },
                       ".MuiSelect-outlined":{
                         fontSize: "14px !important",
                         lineHeight: "20px !important"
                        },
                        ".MuiOutlinedInput-root":{
                          fontSize: "14px"
                      }
                      }}
                      disabled={!isNewVersion && planParams.readOnly}
                    />
                  </LocalizationProvider>
                </FormControl>

                <Autocomplete
                  popupIcon={<KeyboardArrowDownIcon />}
                  size="small"
                  fullWidth
                  disabled={!isNewVersion && planParams.readOnly}
                  options={[1, 2, 3, 4, 5]}
                  getOptionLabel={(week) => `Week ${week}`}
                  getOptionDisabled={(week) => week < currentWeekOfMonth}
                  value={formData.entryWeek ?? planParams.entryWeek ?? null}
                  onChange={(_, week) => {
                    setFormData((prev) => ({ ...prev, entryWeek: week }));
                    if (isUpdateChangesButtonDisabled) setIsUpdateChangesButtonDisabled(false);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} name="entryWeek" label="Week*" size="small" />
                  )}
                  sx={{
                    flex: 1,
                    "& .MuiInputLabel-root": {
                      fontSize: "14px",
                      fontWeight: 600,
                      color: !isNewVersion && planParams.readOnly ? "#919EABCC !important" : "",
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      color: "#919EABCC",
                      WebkitTextFillColor: "#919EABCC",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: !isNewVersion && planParams.readOnly ? "#919EAB33 !important" : "",
                      borderStyle: !isNewVersion && planParams.readOnly ? "dashed !important" : "solid",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: !isNewVersion && planParams.readOnly ? "#919EAB33 !important" : "",
                    },
                  }}
                />
              </FormControl>
            )}
            <Box sx={{ mt: 2 }}>
              {!isNewVersion && (
                <Box sx={{ ...(showPulse && modalOpenedFrom === 'readOnly' ? pulseStyle : {}), py: '4px', width: "180px" }}>
                  <Tooltip
                    title={lockPlan ? "" : lockPermissionTooltipPlan}
                    placement="right"
                    disableHoverListener={lockPlan}
                  >
                    <span>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={formData.readOnly || false}
                            onChange={handleCheckboxChange}
                            name="readOnly"
                            sx={{ m: "1px" }}
                            disabled={!lockPlan}
                          />
                        }
                        label="Make read only"
                        sx={{ display: "block", ".MuiFormControlLabel-label":{
                                        fontSize: "14px !important",
                                        color: !lockPlan ? "#919EAB !important" : "",
                                      }}}
                      />
                    </span>
                  </Tooltip>
                </Box>
              )}
              {/* <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.autoRefreshRevDaily || false}
                    onChange={handleCheckboxChange}
                    name="autoRefreshRevDaily"
                    sx={{ m: "1px" }}
                  />
                }
                label="Rev auto refresh on"
                sx={{ display: "block", mb: 2 }}
              /> */}

              <Box sx={{ ...(showPulse && modalOpenedFrom === 'planStatus' ? pulseStyle : {}), py: '4px', width: "180px" }}>
                <Tooltip
                  title={lockPlan ? "" : lockPermissionTooltipPlan}
                  placement="right"
                  disableHoverListener={lockPlan}
                >
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={formData.planStatus === "Published"}
                          onChange={handleCheckboxChange}
                          name="planStatus"
                          sx={{
                            m: "1px",
                            "&.Mui-checked": {
                              color: publishedStatusCheckboxDisabled ? "#A7B1BC" : "#2A5BA2",
                            },
                          }}
                          disabled={publishedStatusCheckboxDisabled}
                        />
                      }
                      label='Mark as “Published”'
                      sx={{
                        display: "block",
                        ".MuiFormControlLabel-label": {
                          fontSize: "14px !important",
                          color: publishedStatusCheckboxDisabled ? "#919EAB !important" : "",
                        },
                      }}
                    />
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 0,
            mt: 2,
          }}
        >
          <Button
            onClick={handleClose}
            sx={{ color: "#212B36", borderColor: "#919EAB52" }}
            variant="outlined"
          >
            Cancel
          </Button>

          {!isNewVersion && (
            <Button
              variant="contained"
              onClick={updatePlanSettings}
              disabled={isUpdateChangesButtonDisabled}
              className={`!ml-0 !font-bold text-sm ${isUpdateChangesButtonDisabled ? "!bg-[#919EAB33] !text-[#919EABCC]" : ""}`}
            >
              Update version settings
            </Button>
          )}

          {isNewVersion && (
            <Button 
              className="!ml-0"
              variant="contained" 
              onClick={createNewVersion}
              disabled={!formData.versionName || !formData.versionName.trim()}
            >
              Save as new version
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlanVersionModal;